/**
 * DAM live mode — Phase 3 Prompt 12 of the Datacrate Model rollout.
 *
 * `VITE_DAM_LIVE_MODE` is the feature flag that cuts a report type's
 * generation over from the inline tag-match path to the DAM-backed
 * context (`buildDamContext`). It is intentionally SEPARATE from
 * `VITE_DAM_SHADOW_MODE` (Phase 2) — shadow mode logs a comparison
 * without changing the report; live mode actually swaps the source of
 * truth.
 *
 * Rollout discipline (mirrors the pack):
 *  - Default ON for every type in `LIVE_ENABLED_TYPES` below: the 6
 *    original DAM types as of DLA (row 0.99231, 2026-05-18), plus
 *    custom-brief since 2026-05-21 (d165b6368), so 7 of the 11 report
 *    types. `cloudbuild.yaml` substitutes `_DAM_LIVE_MODE='true'`
 *    by default, which is passed as the `VITE_DAM_LIVE_MODE` build-arg
 *    so the production bundle carries it.
 *  - `npm run verify:dam` checks every DAM in `LIVE_ENABLED_TYPES` —
 *    if any fails the schema / verification / refs checks, do NOT
 *    redeploy with the flag on. It also prints `[STALE]` for a DAM whose
 *    source manifest differs from the checkout (warn-only unless
 *    `--fail-on-stale`, which exits 2).
 *  - Rollback (NOT zero-redeploy — VITE_ vars bake at build time):
 *    redeploy with `--substitutions=_DAM_LIVE_MODE=false`. Per-type
 *    rollback: remove the offending type from `LIVE_ENABLED_TYPES`
 *    below and redeploy.
 *  - Historical note: Phase 3 shipped feasibility-only; DM5/DM6
 *    soaked feasibility on the DAM; Phase 4 widened to all 6 with
 *    the skip-shadow-go-straight-to-live strategy. DLA flipped the
 *    `cloudbuild.yaml` default from 'false' to 'true' so every prod
 *    build inherits live-on.
 */
import type { ReportType } from '../../types/datacrateModel';

/** Report types the live cutover currently covers. Phase 3 = feasibility
 *  only; Phase 4 widened to the 6 DAM types; custom-brief joined on
 *  2026-05-21 (d165b6368).
 *  2026-05-14: widened simultaneously with SHADOW_ENABLED_TYPES per the
 *  skip-shadow-go-straight-to-live strategy revision (docs/plan/2026-Q2.md §3).
 *  All 6 DAMs (feasibility / environmental / power-availability / water-resource /
 *  interconnection / test-fit) are in Firestore at ≥95% verification, built today.
 *  Rollback path remains intact — redeploy without `_DAM_LIVE_MODE=true` flips
 *  every type back to the inline path simultaneously in <10 min. Per-type rollback
 *  is also available by removing a single type from this set + redeploying. */
/**
 * Exported as `ReadonlySet` so admin surfaces (DamDashboard / DamModeStatusStrip)
 * can derive their chip labels from the SAME source-of-truth set that the
 * runtime gate uses. ADX-DAM-CHIP-FIX (DLA follow-up 2026-05-18): the
 * dashboard's live-mode chip used to render a hardcoded `"ON · all 6 types"`
 * label that silently desynced from a per-type rollback (remove a type
 * from this set → chip still claimed all-six). Now the chip computes
 * `LIVE_ENABLED_TYPES.size` so it stays accurate as the set narrows.
 */
export const LIVE_ENABLED_TYPES: ReadonlySet<ReportType> = new Set([
  'feasibility',
  'environmental',
  'power-availability',
  'water-resource',
  'interconnection',
  'test-fit',
  'custom-brief',
]);

/**
 * True iff the live-mode feature flag is set for this environment AND
 * the report type is in the current rollout phase.
 *
 * The flag is read from `import.meta.env.VITE_DAM_LIVE_MODE` in
 * browser builds and `process.env.VITE_DAM_LIVE_MODE` in Node
 * (CLI / tests / server). Case-insensitive `'true'` only — any other
 * value, including an unset runtime flag, is falsy. Cloud Build defaults
 * the build-time flag to true; a local Node worker can still leave it unset.
 */
export function isDamLiveModeEnabled(reportType: ReportType): boolean {
  if (!LIVE_ENABLED_TYPES.has(reportType)) return false;
  const raw = readEnvFlag('VITE_DAM_LIVE_MODE');
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'true';
}

function readEnvFlag(name: string): string | undefined {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env != null) {
      const v = (import.meta.env as Record<string, unknown>)[name];
      if (typeof v === 'string') return v;
    }
  } catch { /* import.meta unavailable in older Node */ }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }
  return undefined;
}
